import React, { useState, useEffect } from 'react';
import { Terminal, Code, Box, Eye, ShieldCheck, CheckCircle2, Loader2, Sparkles } from 'lucide-react';
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/integrations/supabase/AuthProvider";
import { useSonas } from "../SonasContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const ProposedChanges = ({ commands = [] }) => {
  const { activeConversationId, isProjectConnected, onSend } = useSonas();
  const { profile, user } = useAuth();
  const [statuses, setStatuses] = useState({});
  const [previewIdx, setPreviewIdx] = useState(null);
  const isPro = profile?.is_pro || false;

  // Listen for real-time updates from the plugin
  useEffect(() => {
    if (!activeConversationId) return;

    const channel = supabase
      .channel('plugin-sync')
      .on('postgres_changes', { 
        event: 'UPDATE', 
        schema: 'public', 
        table: 'plugin_commands',
        filter: `project_id=eq.${activeConversationId}`
      }, (payload) => {
        if (payload.new.executed) {
          // Find which command this was based on the payload (simplified for now)
          // In a real app, we'd map command IDs to local indices
          toast.success("Script successfully injected into Studio!");
        }
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [activeConversationId]);

  const handleAction = async (index, action) => {
    const cmd = commands[index];
    if (!cmd) return;
    
    if (action === 'accept') {
      if (!isProjectConnected) {
        toast.error("Plugin not connected! Open the project in Studio.");
        return;
      }
      
      setStatuses(prev => ({ ...prev, [index]: 'syncing' }));
      try {
        const { error } = await supabase.from('plugin_commands').insert([{
          project_id: activeConversationId,
          command_type: cmd.className || 'Instance',
          payload: cmd,
          executed: false
        }]);
        
        if (error) throw error;
        setStatuses(prev => ({ ...prev, [index]: 'accepted' }));
      } catch (err) {
        setStatuses(prev => ({ ...prev, [index]: 'pending' }));
        toast.error("Sync error.");
      }
    } else if (action === 'audit') {
      const auditPrompt = `Perform a high-level security and performance audit on this script: \n\n\`\`\`lua\n${cmd.source}\n\`\`\`\n\nIdentify potential memory leaks, infinite loops, or exploit vulnerabilities.`;
      onSend(auditPrompt, { model: "claude-airforce" });
      toast.info("Initializing Logic Audit...");
    } else if (action === 'possess') {
      try {
        const { error } = await supabase.from('possessions').insert([{
          user_id: user.id,
          title: cmd.name,
          type: (cmd.className || '').includes('Script') ? 'script' : 'instance',
          content: cmd.source || JSON.stringify(cmd)
        }]);

        if (error) throw error;
        toast.success(`Possessed: ${cmd.name}`, { icon: '📦' });
      } catch (err) {
        toast.error("Vault sync error.");
      }
    }
  };

  if (!commands || !Array.isArray(commands) || commands.length === 0) return null;

  return (
    <div className="my-6 bg-sonas-surface2/50 border border-sonas-border rounded-2xl overflow-hidden shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-500 w-full">
      <div className="px-4 py-3 bg-white/[0.03] border-b border-sonas-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Terminal className="h-4 w-4 text-sonas-accent" />
          <span className="text-[11px] font-bold uppercase tracking-widest">Architectural Plan</span>
        </div>
        <div className="flex items-center gap-2 px-2 py-0.5 rounded-full bg-sonas-accent/10 border border-sonas-accent/20">
          <div className="h-1 w-1 rounded-full bg-sonas-accent animate-pulse" />
          <span className="text-[8px] font-bold text-sonas-accent uppercase">Live Tracked</span>
        </div>
      </div>
      <div className="divide-y divide-sonas-border">
        {commands.map((cmd, i) => {
          if (!cmd) return null;
          const status = statuses[i] || 'pending';
          const isScript = (cmd.className || '').includes('Script');
          const isPreviewing = previewIdx === i;
          
          return (
            <div key={i} className="flex flex-col">
              <div className={cn("p-4 flex items-center justify-between gap-4 transition-all", (status === 'accepted') && "bg-sonas-accent/5")}>
                <div className="flex items-center gap-3">
                  <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center border", isScript ? "border-sonas-accent/30 bg-sonas-accent/10" : "border-blue-500/30 bg-blue-500/10")}>
                    {isScript ? <Code className="h-4 w-4 text-sonas-accent" /> : <Box className="h-4 w-4 text-blue-500" />}
                  </div>
                  <div>
                    <p className="text-xs font-bold truncate max-w-[140px]">{cmd.name}</p>
                    <p className="text-[9px] text-sonas-dim uppercase tracking-widest">{cmd.parent || 'Workspace'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setPreviewIdx(isPreviewing ? null : i)} className={cn("p-1.5 rounded-lg transition-colors", isPreviewing ? "text-sonas-accent bg-sonas-accent/10" : "text-sonas-dim hover:text-sonas-text")} title="Preview Code">
                    <Eye className="h-4 w-4" />
                  </button>
                  {isScript && (
                    <button onClick={() => handleAction(i, 'audit')} className="p-1.5 rounded-lg text-sonas-dim hover:text-blue-400 hover:bg-blue-400/10 transition-all" title="Logic Audit">
                      <ShieldCheck className="h-4 w-4" />
                    </button>
                  )}
                  <button onClick={() => handleAction(i, 'possess')} className="p-1.5 rounded-lg text-sonas-dim hover:text-sonas-accent hover:bg-sonas-accent/10 transition-all" title="Add to Possessions">
                    <Box className="h-4 w-4" />
                  </button>
                  
                  {status === 'pending' ? (
                    <button 
                      onClick={() => handleAction(i, 'accept')} 
                      disabled={!isProjectConnected} 
                      className={cn("px-3 py-1.5 rounded-lg text-[10px] font-bold transition-all", isProjectConnected ? "bg-sonas-accent text-sonas-bg hover:opacity-90" : "bg-white/5 text-sonas-dim")}
                    >
                      Apply
                    </button>
                  ) : status === 'syncing' ? (
                    <div className="px-3 py-1.5 rounded-lg bg-white/5 text-sonas-dim">
                      <Loader2 className="h-3 w-3 animate-spin" />
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sonas-accent/20 text-sonas-accent text-[10px] font-bold uppercase">
                      <CheckCircle2 className="h-3 w-3" /> Injected
                    </div>
                  )}
                </div>
              </div>
              {isPreviewing && isScript && (
                <div className="px-4 pb-4 animate-in slide-in-from-top-1 duration-200">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[9px] font-bold uppercase text-sonas-dim">Source Buffer</p>
                    <button onClick={() => handleAction(i, 'audit')} className="flex items-center gap-1.5 text-[9px] font-bold text-blue-400 hover:text-blue-300">
                      <Sparkles size={10} /> Deep Audit
                    </button>
                  </div>
                  <pre className="p-3 rounded-xl bg-black/40 text-[10px] font-mono text-sonas-accent/80 overflow-x-auto border border-white/5 max-h-40 sonas-scrollbar">
                    {cmd.source || '-- No source code found'}
                  </pre>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProposedChanges;